// Action types for reducer
export const ACTIONS = {
  SET_LOADING: 'set_loading',
  SET_ERROR: 'set_error',
  SET_FLOWERS: 'set_flowers',
  SET_FILTER_OPTIONS: 'set_filter_options',
  SET_COLOR_FILTER: 'set_color_filter',
  SET_CATEGORY_FILTER: 'set_category_filter',
  CLEAR_ALL_FILTERS: 'clear_all_filters',
  TOGGLE_FILTER_DROPDOWN: 'toggle_filter_dropdown',
  UPDATE_FLOWER_QUANTITY: 'update_flower_quantity',
  FORCE_RERENDER: 'force_rerender',
};

// Initial state
export const initialState = {
  allFlowers: [], // All flowers from API (source of truth)
  filteredFlowers: [], // Flowers after filtering
  selectedFlowers: [], // Flowers with quantity > 0
  colorOptions: ['All'], // Available color options
  categoryOptions: ['All'], // Available category options
  activeColorFilter: 'All', // Current color filter
  activeCategoryFilter: 'All', // Current category filter
  filterDropdownOpen: false, // Filter dropdown state
  isLoading: true, // Loading state
  error: null, // Error state
  renderKey: Date.now(), // Key for forcing re-renders
};

// Pure function for filtering flowers
export const getFilteredFlowers = (flowers, colorFilter, categoryFilter) => {
  return flowers.filter((flower) => {
    const matchesColor = colorFilter === 'All' || flower.color === colorFilter;
    const matchesCategory =
      categoryFilter === 'All' || flower.category === categoryFilter;
    return matchesColor && matchesCategory;
  });
};

// Pure function for updating flower quantity
export const updateFlowerQuantity = (flowers, id, change) => {
  return flowers.map((flower) => {
    if (flower.id === id) {
      const newQuantity = Math.max(0, flower.quantity + change);
      return { ...flower, quantity: newQuantity };
    }
    return flower;
  });
};

// Reducer function
export const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };

    case ACTIONS.SET_FLOWERS: {
      const flowers = action.payload;
      // When setting initial flowers, we also set the filtered flowers to the same value
      const filteredFlowers = getFilteredFlowers(
        flowers,
        state.activeColorFilter,
        state.activeCategoryFilter
      );
      const selectedFlowers = flowers.filter((flower) => flower.quantity > 0);

      return {
        ...state,
        allFlowers: flowers,
        filteredFlowers: filteredFlowers,
        selectedFlowers: selectedFlowers,
        isLoading: false,
        renderKey: Date.now(),
      };
    }

    case ACTIONS.SET_FILTER_OPTIONS:
      return {
        ...state,
        colorOptions: ['All', ...action.payload.colors],
        categoryOptions: ['All', ...action.payload.categories],
      };

    case ACTIONS.SET_COLOR_FILTER: {
      const newColorFilter = action.payload;
      // Recompute filtered flowers based on new color filter
      const filteredFlowers = getFilteredFlowers(
        state.allFlowers,
        newColorFilter,
        state.activeCategoryFilter
      );

      return {
        ...state,
        activeColorFilter: newColorFilter,
        filteredFlowers: filteredFlowers,
        renderKey: Date.now(),
      };
    }

    case ACTIONS.SET_CATEGORY_FILTER: {
      const newCategoryFilter = action.payload;
      // Recompute filtered flowers based on new category filter
      const filteredFlowers = getFilteredFlowers(
        state.allFlowers,
        state.activeColorFilter,
        newCategoryFilter
      );

      return {
        ...state,
        activeCategoryFilter: newCategoryFilter,
        filteredFlowers: filteredFlowers,
        renderKey: Date.now(),
      };
    }

    case ACTIONS.CLEAR_ALL_FILTERS: {
      // When clearing filters, we reset to show all flowers
      const filteredFlowers = getFilteredFlowers(
        state.allFlowers,
        'All',
        'All'
      );

      return {
        ...state,
        activeColorFilter: 'All',
        activeCategoryFilter: 'All',
        filteredFlowers: filteredFlowers,
        filterDropdownOpen: false,
        renderKey: Date.now(),
      };
    }

    case ACTIONS.TOGGLE_FILTER_DROPDOWN:
      return {
        ...state,
        filterDropdownOpen:
          action.payload !== undefined
            ? action.payload
            : !state.filterDropdownOpen,
      };

    case ACTIONS.UPDATE_FLOWER_QUANTITY: {
      const { id, change } = action.payload;
      // Update quantity in both all flowers and filtered flowers arrays
      const updatedAllFlowers = updateFlowerQuantity(
        state.allFlowers,
        id,
        change
      );
      const updatedFilteredFlowers = updateFlowerQuantity(
        state.filteredFlowers,
        id,
        change
      );

      // Update selected flowers based on the updated all flowers
      const selectedFlowers = updatedAllFlowers.filter(
        (flower) => flower.quantity > 0
      );

      return {
        ...state,
        allFlowers: updatedAllFlowers,
        filteredFlowers: updatedFilteredFlowers,
        selectedFlowers: selectedFlowers,
        renderKey: Date.now(),
      };
    }

    case ACTIONS.FORCE_RERENDER:
      return {
        ...state,
        renderKey: Date.now(),
      };

    default:
      return state;
  }
};
